import { IApiPageFilterParams, IOption, ISelectPaginateProps, SelectPaginate } from '@gcl/react-core';
import * as React from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';

export interface IUseFetchOptionsResult<T> {
  options: IOption<T>[];
  pageSize: number;
  pageNumber: number;
  totalItemCount: number;
}
export interface IUseFetchOptionsKeywordProps {
  keyword: string;
}
export interface IUseFetchOptionsPropsBase extends IUseFetchOptionsKeywordProps {
  pageFilter?: IApiPageFilterParams;
}

export interface ISelectAsyncPaginateProps<T, UseFetchOptionsProps extends IUseFetchOptionsPropsBase>
  extends Omit<ISelectPaginateProps<IOption<T>>, 'options' | 'onSearch' | 'onPaginate' | 'isLoading' | 'hasMore'> {
  useFetchOptions: (useFetchOptionsProps: UseFetchOptionsProps) => UseQueryResult<IUseFetchOptionsResult<T>, unknown>;
  useFetchOptionsProps?: Omit<UseFetchOptionsProps, 'keyword' | 'pageFilter'>;
  keyFieldName?: string;
  isPaginate?: boolean;
}

export const SelectAsyncPaginate: <T, UseFetchOptionsProps extends IUseFetchOptionsPropsBase>(
  props: ISelectAsyncPaginateProps<T, UseFetchOptionsProps>,
) => React.ReactElement<ISelectAsyncPaginateProps<T, UseFetchOptionsProps>> = ({
  value = [],
  pageSize,
  useFetchOptions,
  useFetchOptionsProps = {},
  keyFieldName = 'key',
  displayFieldName = 'text',
  isPaginate = true,
  ...rest
}) => {
  const [optionsMap, setOptionsMap] = React.useState<{ [key: string]: typeof value }>({});
  const [localOptions, setLocalOptions] = React.useState<typeof value>([]);
  const [keyword, setKeyword] = React.useState<string | undefined>(undefined);
  const [pageFilter, setPageFilter] = React.useState<IApiPageFilterParams>({ skip: 0, take: pageSize });
  const [hasMore, setHasMore] = React.useState<boolean>(!!isPaginate);

  const { isLoading, data } = useFetchOptions({
    ...useFetchOptionsProps,
    keyword,
    pageFilter,
  } as any);

  React.useEffect(() => {
    setOptionsMap({});
  }, [keyword]);

  useDeepCompareEffectNoCheck(() => {
    if (isLoading || !data || Object.keys(data).length <= 0) return;

    const { options, pageNumber, totalItemCount } = data;
    setHasMore(totalItemCount > pageNumber * data.pageSize);
    setOptionsMap((old) => {
      return { ...old, [pageNumber]: options };
    });
  }, [JSON.stringify(data)]);

  useDeepCompareEffectNoCheck(() => {
    setLocalOptions(([] as typeof value).concat.apply([], Object.values(optionsMap)));
  }, [JSON.stringify(optionsMap)]);

  return (
    <SelectPaginate
      {...rest}
      value={value}
      displayFieldName={displayFieldName}
      pageSize={pageSize}
      options={localOptions}
      isLoading={isLoading}
      hasMore={hasMore}
      onSearch={({ pageFilter: filter, keyword: searchKeyword, isKeywordChanged }) => {
        setPageFilter(filter);
        setKeyword(searchKeyword);
      }}
    />
  );
};
